import type { BookingDetail, StayPoint } from '@/shared/api'
import { guestDisplayName, pluralizeRu } from '@/shared/lib/format'
import { SectionCard } from '@/shared/ui'
import s from './BookingStayCard.module.css'

/**
 * Секция 1 — «Шапка брони» (prototype/Prototype.dc.html:2321, первая секция левой колонки).
 *
 * Единственная из шести секций **без eyebrow**: заголовок здесь — само имя гостя.
 *
 * Слева: имя (Fraunces 27px) и мета-строка — телефон, число гостей, объект, пилюля визита.
 * Справа: крупные даты заезда и выезда (Fraunces 40px) с коннектором между ними — пилюлей
 * ночей, линией со стрелкой-треугольником и акцентной строкой `highlight`.
 */
export function BookingStayCard({ booking }: { booking: BookingDetail }) {
  const { guest, object, stay, highlight, blockReasonLabel } = booking
  // Решает СТАТУС: причина в контракте необязательна, и без неё ответ схеме не противоречит.
  // Нет причины — называем период серверной подписью статуса, своей фразы не сочиняем.
  const isBlock = booking.status === 'blocked'
  const blockTitle = blockReasonLabel ?? booking.statusLabel

  return (
    <SectionCard className={s.card}>
      <div className={s.body}>
        <div className={s.row}>
          <div className={s.who}>
            {/*
             * `<h1>` — так в макете, и внутри слоя это единственный заголовок: остальные секции
             * озаглавлены eyebrow'ами (`SectionCard` вешает их через `aria-labelledby`, а не
             * заголовками). Заголовок страницы под карточкой скринридеру не виден — Radix прячет
             * весь остальной документ через `aria-hidden` (см. `BookingModal`).
             */}
            {/*
             * Имя МОЖЕТ БЫТЬ ПУСТЫМ (§3) — тогда здесь стояла бы пустая строка, то есть у экрана не
             * было бы заголовка вовсе. Телефон при этом уже стоит строкой ниже и опознаёт гостя.
             */}
            {/*
             * ⚠️ У ЗАКРЫТОГО ПЕРИОДА ЗАГОЛОВОК — ПРИЧИНА, А НЕ ИМЯ. «Без имени» здесь называло бы
             * гостя, которого не существует: приезжать некому, период закрыт обслуживанием или
             * собственником. Телефона и числа гостей у него тоже нет — не «ноль гостей», а нет
             * самой величины, поэтому вся мета-строка про гостя из шапки уходит.
             */}
            <h1 className={s.name}>{isBlock ? blockTitle : guestDisplayName(guest.name)}</h1>

            <div className={s.meta}>
              {!isBlock && (
                <>
                  <span className={s.phone}>{guest.phone}</span>
                  <span className={s.sep} aria-hidden="true">
                    ·
                  </span>
                  <span>
                    {guest.guestsCount} {pluralizeRu(guest.guestsCount, ['гость', 'гостя', 'гостей'])}
                  </span>
                  <span className={s.sep} aria-hidden="true">
                    ·
                  </span>
                </>
              )}
              <span>
                {object.code} · {object.typeLabel}
              </span>
              {/* `visitLabel` в контракте необязателен: у гостя, приехавшего впервые, пилюли нет. */}
              {guest.visitLabel && <span className={s.visit}>{guest.visitLabel}</span>}
            </div>

            {/*
             * Примечание к закрытому периоду — ПОД причиной, потому что оно её и уточняет:
             * «Буфер уборки» отвечает «чем закрыт», примечание — «что при этом имелось в виду».
             * Это разные поля домена (`block_reason` — выбор из закрытого набора, `block_notes` —
             * свободный текст), и склеивать их в одну строку значило бы выдать набранное руками
             * за выбранное из справочника.
             *
             * У брони гостя этого поля не бывает вовсе — домен принимает его только у блокировки.
             */}
            {isBlock && booking.blockNotes && (
              <p className={s.blockNotes}>{booking.blockNotes}</p>
            )}
          </div>

          <div className={s.stay}>
            <StayPointView eyebrow="Заезд" point={stay.checkIn} />

            <div className={s.connector}>
              <span className={s.nights}>
                {stay.nights} {pluralizeRu(stay.nights, ['ночь', 'ночи', 'ночей'])}
              </span>
              {/* Линия со стрелкой — CSS-треугольник на псевдоэлементе, как в макете (там же
                  он `<span>` с border-трюком). SVG для этого не нужен. */}
              <span className={s.line} aria-hidden="true" />
              {/*
               * Тот же `highlight`, что и бейджем в шапке модалки, — в макете он и правда
               * напечатан дважды (бейдж наверху и акцентная строка здесь). `null` → строки нет,
               * коннектор просто короче.
               */}
              {highlight && <span className={s.highlight}>{highlight}</span>}
            </div>

            <StayPointView eyebrow="Выезд" point={stay.checkOut} />
          </div>
        </div>
      </div>
    </SectionCard>
  )
}

/**
 * Колонка одной даты: подпись, крупное число с месяцем, время и день недели.
 *
 * Все части приходят готовыми (`StayPoint`: `dom`, `monthLabel`, `time`, `dowLabel`) — фронт
 * даты не форматирует и про локаль не знает (инвариант контракта: «даты и подписи считает
 * сервер»). Подписи «Заезд» / «Выезд» — не данные, а хром интерфейса, они из макета.
 */
function StayPointView({ eyebrow, point }: { eyebrow: string; point: StayPoint }) {
  return (
    <div className={s.point}>
      <div className={s.pointEyebrow}>{eyebrow}</div>
      <div className={s.pointDate}>
        <span className={s.dom}>{point.dom}</span>
        <span className={s.month}>{point.monthLabel}</span>
      </div>
      <div className={s.time}>
        {point.time} · {point.dowLabel}
      </div>
    </div>
  )
}
